import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import type { StepSubscriptions } from "@stepfinance/common";
import { convert } from "@stepfinance/common";
import { StepSubscriptionsIDL } from "@stepfinance/common";
import {
  ContextButton,
  lightGreen,
  lightGrey3,
  SPACE_MONO,
  StepCard,
  StepInfoBox,
  StepSpinner,
  TagInfo,
  transparentWhite,
  white,
} from "@stepfinance/react";
import { Col, Row, Tooltip } from "antd";
import { BN } from "bn.js";
import type { FC } from "react";
import { useEffect } from "react";
import { useMemo, useState } from "react";
import shallow from "zustand/shallow";

import { SUBSCRIPTIONS_PROGRAM_ID } from "../../actions/step/tiers/constants";
import { acquireTier } from "../../actions/step/tiers/tier";
import { upgradeTier } from "../../actions/step/tiers/tier";
import { ReactComponent as InfoIcon } from "../../assets/info.svg";
import { useAccountsContext } from "../../contexts/accounts";
import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useMarkets } from "../../contexts/market";
import { useConnectedWallet } from "../../hooks";
import { useTransactionCacheStatus } from "../../hooks/queries/transactionHistory/useTransactionCacheStatus";
import { useTransactionMonthStats } from "../../hooks/queries/transactionHistory/useTransactionMonthStats";
import { useTransactionTiers } from "../../hooks/queries/transactionHistory/useTransactionTiers";
import { useUserTier } from "../../hooks/queries/transactionHistory/useUserTier";
import { useStepAuthStore } from "../../hooks/stores/stepAuth";
import { useTransactionHistoryStore } from "../../hooks/stores/transactionHistoryStore";
import { useAnchorProgram } from "../../hooks/useAnchorProgram";
import { getTokenByMint } from "../../utils/tokenInfo";
import { createRange, formatStringWithPlaceholders } from "../../utils/utils";
import { TokenInfoDisplay } from "../AmountInput/TokenInfoDisplay";
import { InfoCard } from "../InfoCard/InfoCard";
import { LoadingErrorMessage } from "../LoadingErrorMessage/LoadingErrorMessage";
import { Modal } from "../Modal/Modal";
import { TextDescription, TextTitle } from "../shared";
import { StepSkeleton } from "../StepSkeleton/StepSkeleton";

//MOVE to COMMON
const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

// The calls are made in the parent component(calendar page), and not when the modal is invoked. Check how to fix this.
export const TiersModal: FC = () => {
  const cacheStatusQuery = useTransactionCacheStatus(false);
  const [userTierPurchase, setUserTierPurchase] = useState({
    loading: false,
    tierName: "",
  });
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const { publicKey } = useEnrichedWallet();
  const [userBalance, setUserBalance] = useState(0);
  const [isUserBalanceLoading, setIsUserBalanceLoading] = useState(false);
  const [selectedYear, setYearsAvailable] = useTransactionHistoryStore(
    (state) => [state.selectedYear, state.setYearsAvailable],
    shallow,
  );
  const refreshJwt = useStepAuthStore((state) => state.refreshJwt);
  const [tiersYear, setTiersYear] = useState(selectedYear);
  const { data: monthlyTransactions, refetch: monthlyTransactionsRefetch } = useTransactionMonthStats(tiersYear);
  const {
    data: tiers,
    isLoading: isTiersLoading,
    isSuccess: isTiersSuccess,
    isError: isTiersError,
    refetch: refetchTiers,
  } = useTransactionTiers(tiersYear);
  const {
    currentLanguageData: {
      components: { tiersModal: tiersModalLanguage },
    },
  } = useLanguageContext();
  const [tiersModalOpen, setTiersModalOpen] = useTransactionHistoryStore(
    (state) => [state.tiersModalOpen, state.setTiersModalOpen],
    shallow,
  );
  const { userTokenAccounts } = useAccountsContext();
  const { marketsInitialised } = useMarkets();
  const { network } = useNetworkConfig();

  const totalTransactions = useMemo(() => {
    const monthStats = monthlyTransactions;
    if (monthStats) {
      return monthStats.reduce((accum, month) => {
        const { fee_payer_count } = month;
        return accum + fee_payer_count || 0;
      }, 0);
    }
    return 0;
  }, [monthlyTransactions]);

  const program = useAnchorProgram<StepSubscriptions>(StepSubscriptionsIDL, SUBSCRIPTIONS_PROGRAM_ID);

  const { data: userTier, isLoading: isUserTierLoading } = useUserTier(tiersYear, tiers);

  const isFullTier = (transactionsToUnlock: number) => {
    return transactionsToUnlock > 1_000_000;
  };

  useEffect(() => {
    const fetchUserBalance = async () => {
      if (publicKey && userTokenAccounts.length > 0 && tiers && tiers.length > 0 && marketsInitialised) {
        const mint = tiers[0].price.mint;
        const tokenAccount = userTokenAccounts.find((tokenAccount) => tokenAccount.info.mint === mint);
        setUserBalance(tokenAccount?.tokenAmount.uiAmount ?? 0);
      } else if (publicKey && tiers && tiers.length > 0 && network === "devnet") {
        // TODO: This is only here for DEVNET!!! Remove this and only rely on user token accounts fetched on load
        setIsUserBalanceLoading(true);
        const tier = tiers[0];
        const userTokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
          mint: new PublicKey(tier.price.mint),
        });
        setIsUserBalanceLoading(false);
        const stepTokenAccount = userTokenAccounts.value[0];
        if (stepTokenAccount) {
          const formattedTokenBalance = stepTokenAccount.account.data.parsed.info.tokenAmount.uiAmount ?? 0;
          setUserBalance(formattedTokenBalance);
        }
      } else {
        setUserBalance(0);
      }
    };
    void fetchUserBalance();
  }, [connection, publicKey, tiers, userTier, userTokenAccounts, marketsInitialised, network]);

  const filteredTiers = useMemo(() => {
    if (tiers && userTier) {
      return tiers.filter((tier) => tier.tierLevel > userTier.tierLevel);
    }
    return tiers;
  }, [tiers, userTier]);

  const formattedTiers = useMemo(() => {
    return filteredTiers?.map((tier) => {
      const transactionsToUnlock = Number(tier.totalTransactionsToUnlock);
      const mintDecimals = getTokenByMint(tier.price.mint)?.decimals;
      // If user upgraded already, this variable will have the amount he paid, otherwise it's 0;
      const totalPaidAlready = userTier ? convert(new BN(userTier.totalPaid), mintDecimals) : 0;
      const tierCost = convert(new BN(tier.price.cost), mintDecimals) - totalPaidAlready;

      const transactionsSurplus = transactionsToUnlock - totalTransactions;

      return {
        ...tier,
        tierCost,
        transactionsSurplus,
      };
    });
  }, [filteredTiers, totalTransactions, userTier]);

  const isCurrentYear = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return tiersYear === currentYear;
  }, [tiersYear]);

  useEffect(() => {
    setTiersYear(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    if (cacheStatusQuery.data?.oldestCachedDateTime) {
      const oldestYear = new Date(cacheStatusQuery.data.oldestCachedDateTime).getFullYear();
      setYearsAvailable(createRange(currentYear, oldestYear));
    } else {
      setYearsAvailable([currentYear]);
    }
  }, [cacheStatusQuery.data?.oldestCachedDateTime, setYearsAvailable]);

  useEffect(() => {
    void refetchTiers();
    void monthlyTransactionsRefetch();
  }, [monthlyTransactionsRefetch, refetchTiers, tiersYear]);

  const onPurchaseClick = async (tierName: string, tierId: string, costMint: string, currentTierId?: string) => {
    setUserTierPurchase({
      loading: true,
      tierName: tierName,
    });
    if (wallet && program) {
      try {
        if (currentTierId) {
          await upgradeTier(
            program,
            connection,
            wallet,
            tierId,
            costMint,
            currentTierId,
            tiersModalLanguage.notifications,
          );
        } else {
          await acquireTier(program, connection, wallet, tierId, costMint, tiersModalLanguage.notifications);
        }
        await refreshJwt(network);
        setTiersModalOpen(false);
      } catch (e) {
        console.error("Tier Purchase Error:", e);
      }
    }
    setUserTierPurchase({
      loading: false,
      tierName: "",
    });
  };

  const formatUnlockDate = (tierUnlockDate?: string) => {
    if (tierUnlockDate) {
      const formattedDate = new Date(tierUnlockDate.replace("Z", ""));
      const currentYear = new Date().getFullYear();
      if (formattedDate.getFullYear() > tiersYear) {
        // if it's 2022, 2021, 2020, etc
        if (currentYear > tiersYear) {
          return tiersModalLanguage.endOfYear;
        } else {
          // By today we mean "all transactions the user has + additional ones he hasn't used to unlock new txs yet"
          return tiersModalLanguage.today;
        }
      } else {
        return DATE_FORMATTER.format(formattedDate);
      }
    } else {
      // If there's no unlock date, then it's the unlimited tier
      return tiersModalLanguage.endOfYear;
    }
  };

  return (
    <Modal
      onClose={() => setTiersModalOpen(false)}
      visible={tiersModalOpen}
      title={tiersModalLanguage.unlockTransactions}
      width={685}
    >
      <ContentContainer>
        <p className="label">{tiersModalLanguage.forYear + " " + tiersYear}</p>
        {formattedTiers && formattedTiers.length > 0 && (
          <div className="tiers-title-container">
            <div className="unlock-title-container">
              <p className="unlock-title">{tiersModalLanguage.youCanUnlock}</p>
              <Tooltip title={tiersModalLanguage.transactionsFree} placement="bottom">
                <InfoIcon />
              </Tooltip>
            </div>
            <TagInfo tagType="text" useSpaceMono={false} uppercase={false}>
              {isTiersError && <>{tiersModalLanguage.noTransactionsFound}</>}
              {isTiersLoading && <StepSpinner style={{ color: white, marginRight: 8 }} size={10} />}
              {(isTiersLoading || isTiersSuccess) &&
                formatStringWithPlaceholders(
                  tiersModalLanguage.transactionsMade,
                  isTiersLoading ? undefined : totalTransactions,
                )}
            </TagInfo>
          </div>
        )}
        {formattedTiers && formattedTiers.length > 0 && (
          <div className="tier-list-container">
            {isTiersError ? (
              <div className="error-container">
                <LoadingErrorMessage onRetryClick={refetchTiers}>
                  {tiersModalLanguage.errorLoadingTiers}
                </LoadingErrorMessage>
              </div>
            ) : (
              <Row gutter={[16, 30]}>
                {(isTiersLoading || isUserTierLoading) &&
                  [1, 2, 3, 4].map((_, i) => (
                    <Col md={12} sm={{ span: 24 }} xs={24} key={i}>
                      <TierSkeleton />
                    </Col>
                  ))}
                {formattedTiers &&
                  formattedTiers.length > 0 &&
                  formattedTiers.map((tier) => {
                    return (
                      <Col md={12} sm={{ span: 24 }} xs={24} key={tier.name}>
                        <div>
                          <TextTitle className="tier-title" size="s">
                            {tier.name}
                          </TextTitle>
                          <StepCard type="secondary">
                            <div className="info-box-container">
                              <StepInfoBox>
                                <StepInfoBox.Description>
                                  <TextDescription>{tiersModalLanguage.transactionsToUnlock}</TextDescription>
                                </StepInfoBox.Description>
                                <StepInfoBox.Title>
                                  <TextTitle size="s">
                                    {isFullTier(Number(tier.totalTransactionsToUnlock))
                                      ? formatStringWithPlaceholders(tiersModalLanguage.allTransactionsIn, tiersYear)
                                      : formatStringWithPlaceholders(
                                          tiersModalLanguage.transactionsAmount,
                                          tier.totalTransactionsToUnlock,
                                        )}
                                  </TextTitle>
                                </StepInfoBox.Title>
                              </StepInfoBox>
                              <StepInfoBox>
                                <StepInfoBox.Description>
                                  <div className="transactions-to-unlock">
                                    <TextDescription>{tiersModalLanguage.unlocksTransactionsUpTo}</TextDescription>
                                  </div>
                                </StepInfoBox.Description>
                                <StepInfoBox.Title>
                                  <div className="info-box-title-container">
                                    <>
                                      <TextTitle size="s">
                                        {isFullTier(Number(tier.totalTransactionsToUnlock)) &&
                                          formatUnlockDate(undefined)}
                                        {!isFullTier(Number(tier.totalTransactionsToUnlock)) &&
                                          tier.maxDateOfUnlock &&
                                          formatUnlockDate(tier.maxDateOfUnlock)}
                                      </TextTitle>
                                    </>
                                  </div>
                                </StepInfoBox.Title>
                              </StepInfoBox>
                              <StepInfoBox>
                                <StepInfoBox.Description>
                                  <TextDescription>{tiersModalLanguage.additionalTransactions}</TextDescription>
                                </StepInfoBox.Description>
                                <StepInfoBox.Title>
                                  <div className="info-box-title-container">
                                    {isCurrentYear &&
                                    tier.transactionsSurplus > 0 &&
                                    !isFullTier(Number(tier.totalTransactionsToUnlock)) ? (
                                      <TextTitle size="s">{tier.transactionsSurplus}</TextTitle>
                                    ) : (
                                      <TextTitle size="s">--</TextTitle>
                                    )}
                                  </div>
                                </StepInfoBox.Title>
                              </StepInfoBox>
                              <StepInfoBox>
                                <StepInfoBox.Description>
                                  <TextDescription>{tiersModalLanguage.forTheCostOf}</TextDescription>
                                </StepInfoBox.Description>
                                <StepInfoBox.Title>
                                  <div className="info-box-title-container">
                                    <TextTitle number>{tier.tierCost}</TextTitle>
                                    <TokenInfoDisplay mintAddress={tier.price.mint} symbol="USDC" />
                                  </div>
                                </StepInfoBox.Title>
                              </StepInfoBox>
                            </div>
                            <div className="buy-container">
                              <div></div>
                              <ContextButton
                                disabled={userBalance < tier.tierCost || isUserBalanceLoading}
                                loading={
                                  (userTierPurchase.loading && userTierPurchase.tierName === tier.name) ||
                                  isUserBalanceLoading
                                }
                                onClick={() => onPurchaseClick(tier.name, tier.id, tier.price.mint, userTier?.id)}
                              >
                                {userBalance < tier.tierCost && !isUserBalanceLoading
                                  ? "Insufficient balance"
                                  : userTier
                                  ? tiersModalLanguage.upgrade
                                  : tiersModalLanguage.buy}
                              </ContextButton>
                            </div>
                          </StepCard>
                        </div>
                      </Col>
                    );
                  })}
              </Row>
            )}
          </div>
        )}
        <InfoCard
          type="warn"
          title={
            formattedTiers && formattedTiers.length > 0
              ? tiersModalLanguage.tiersUpgradeable
              : tiersModalLanguage.maxTierPurchased
          }
        >
          <span>
            {formattedTiers && formattedTiers.length > 0
              ? tiersModalLanguage.upgradeableDescription
              : tiersModalLanguage.maxTierPurchasedDescription}
          </span>
        </InfoCard>
      </ContentContainer>
    </Modal>
  );
};

const TierSkeleton: React.FC = () => {
  return (
    <div>
      <StepSkeleton style={{ marginBottom: 16 }} />
      <StepCard type="secondary">
        <div className="info-box-container">
          <StepInfoBox>
            <StepSkeleton size="s" width={225} />
            <StepSkeleton size="m" width={150} />
          </StepInfoBox>
          <StepInfoBox>
            <StepSkeleton size="s" width={225} />
            <StepSkeleton size="m" width={150} />
          </StepInfoBox>
          <StepInfoBox>
            <StepSkeleton size="s" width={225} />
            <StepSkeleton size="m" width={150} />
          </StepInfoBox>
          <StepInfoBox>
            <StepSkeleton size="s" width={225} />
            <StepSkeleton size="m" width={150} />
          </StepInfoBox>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1 }}></div>
          <StepSkeleton size="l" width={110} />
        </div>
      </StepCard>
    </div>
  );
};

const ContentContainer = styled.div`
  .label {
    color: ${white};
    margin-bottom: 16px;
  }
  .user-stats-container {
    margin-top: 16px;
    .transactions-made-title-container {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  .tiers-title-container {
    display: flex;
    justify-content: space-between;
    margin: 16px 0;
    .unlock-title-container {
      display: flex;
      align-items: center;
      .unlock-title {
        margin: 0;
        margin-right: 4px;
        color: ${white};
      }
    }
  }
  .upgradeable-label {
    margin: 16px 0;
  }
  .tier-list-container {
    height: 500px;
    margin-bottom: 16px;
    overflow-y: auto;
    &::-webkit-scrollbar-thumb {
      background: ${lightGreen};
    }
    &::-webkit-scrollbar-track {
      background: ${lightGrey3};
    }
    .tier-title {
      margin-bottom: 16px;
    }
    .error-container {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .info-box-container {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 10px;
      .transactions-to-unlock {
        display: flex;
        gap: 4px;
        align-items: center;
      }
      .info-box-title-container {
        display: flex;
        align-items: center;
        gap: 10px;
        .discounted-amount {
          font-family: ${SPACE_MONO};
          font-size: 16px;
          color: ${transparentWhite};
          text-decoration-line: line-through;
          display: inline-flex;
          align-items: center;
        }
      }
    }
  }
  .buy-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;
